import { router, usePage } from '@inertiajs/react'
import Layout from '@/components/layout/Layout'
import NoteCard from '@/components/notes/NoteCard'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import { useSync } from '@/hooks/useSync'

interface NoteItem {
  id: string
  title: string
  slug: string
  directory: string
  tags: string[]
  excerpt: string
  updated_at: string
}

interface TagCount {
  tag: string
  count: number
}

interface Props {
  notes: NoteItem[]
  tags: TagCount[]
}

export default function NotesIndex({ notes, tags }: Props) {
  useSync('notes')
  const [sortBy, setSortBy] = useState<'updated' | 'alpha'>('updated')
  const params = new URLSearchParams(window.location.search)
  const activeTag = params.get('tag')

  const filtered = activeTag
    ? notes.filter(n => n.tags.includes(activeTag))
    : notes

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'alpha') return a.title.localeCompare(b.title)
    return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
  })

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Pages</h1>
          <Button onClick={() => router.visit('/notes/new')}>
            <Plus className="mr-1 h-4 w-4" /> New Page
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex gap-1">
            <Button variant={sortBy === 'updated' ? 'secondary' : 'ghost'} size="sm"
              onClick={() => setSortBy('updated')}>
              Recent
            </Button>
            <Button variant={sortBy === 'alpha' ? 'secondary' : 'ghost'} size="sm"
              onClick={() => setSortBy('alpha')}>
              A-Z
            </Button>
          </div>

          {activeTag && (
            <div className="flex items-center gap-1">
              <span className="text-sm text-gray-400">Filtered by:</span>
              <Badge variant="secondary" className="cursor-pointer"
                onClick={() => router.visit('/notes')}>
                {activeTag} ×
              </Badge>
            </div>
          )}
        </div>

        {sorted.length === 0 ? (
          <p className="text-sm text-gray-500">
            {activeTag ? `No pages tagged "${activeTag}".` : 'No pages yet. Create your first page to get started.'}
          </p>
        ) : (
          <div className="space-y-2">
            {sorted.map(note => (
              <NoteCard key={note.id} note={note} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  )
}
